import Vue from 'vue'
import firebase from '@/firebase'

// Which ngtorch a plan, model report or model build ran with. The Cloud Functions freeze the override
// onto the queue item (plans, model reports), the ekrm document (EKRM builds) or the auto model request
// (automatic models) when they boot its server. Overrides only exist in dev, and only admins and
// scientists may read the queue, so everything here is gated on both.

const OVERRIDE_PROJECT = 'ngapp5-dev'

// clientId -> the queue items of that model that ran with an override. Shared by every list and page,
// so a model's items are read once however many rows show them.
const state = Vue.observable({ byClient: {} })
const pending = {}

export function ngtorchRunsVisible (authUser) {
  return firebase.db.app.options.projectId === OVERRIDE_PROJECT &&
    Boolean(authUser) && (authUser.isAdmin === true || authUser.isScientist === true)
}

// date_created comes as a Date (simulations store), a Firestore Timestamp or an ISO string (Home lists).
export function createdMillis (value) {
  if (!value) return null
  if (value instanceof Date) return value.getTime()
  if (typeof value.toMillis === 'function') return value.toMillis()
  if (typeof value === 'string') {
    const millis = Date.parse(value)
    return isNaN(millis) ? null : millis
  }
  if (typeof value === 'number') return value
  return null
}

export function isOverride (override) {
  return Boolean(override) && override.enabled === true && typeof override.sha === 'string'
}

// Only the items that carry an override are read: a model's queue holds a copy of every plan it ever
// ran, and a plan without a matching item here simply ran with the image's own ngtorch.
export function loadOverriddenRuns (clientId, force = false) {
  if (!clientId || typeof clientId !== 'string') return Promise.resolve()
  if (pending[clientId]) return pending[clientId]
  if (!force && state.byClient[clientId]) return Promise.resolve()
  const done = () => { delete pending[clientId] }
  pending[clientId] = firebase.db.collection('queue')
    .where('clientId', '==', clientId)
    .where('ngtorch_override.enabled', '==', true)
    .get()
    .then(snapshot => {
      const runs = snapshot.docs.map(doc => {
        const data = doc.data()
        return {
          simulationId: data.simulationId || null,
          budgetSimulationId: data.budget_simulation_id || null,
          // The queue item keeps the plan's date_created, which tells the versions of an edited plan apart.
          created: createdMillis(data.date_created),
          deleted: data.deleted_simulation === true,
          override: data.ngtorch_override
        }
      })
      Vue.set(state.byClient, clientId, runs)
      done()
    }, error => {
      console.error(`Could not read the ngtorch runs of model ${clientId}`, error)
      done()
    })
  return pending[clientId]
}

// The override the plan's current version ran with, or null when it ran without one (or is not loaded).
export function planOverride (clientId, plan) {
  const runs = state.byClient[clientId]
  if (!runs || !plan) return null
  const created = createdMillis(plan.date_created)
  const run = runs.find(item => item.simulationId === plan.id && item.created === created)
  return run ? run.override : null
}

// The overrides the grid points of a Budget Simulator ran with, for lists that only have the parent.
export function budgetSimulationOverrides (clientId, budgetSimulationId) {
  const runs = state.byClient[clientId]
  if (!runs) return []
  return runs.filter(item => item.budgetSimulationId === budgetSimulationId && !item.deleted).map(item => item.override)
}

// The run that produced the model report on screen: the newest report queue item that completed. One
// still running, or one whose override checkout failed and so never started, has not replaced it.
// Report items are told from plan copies by model_type, which plans do not have. Returns undefined
// when the model has no report item at all (an automatic model's report comes from its build).
export async function reportRunOverride (model) {
  const snapshot = await firebase.db.collection('queue')
    .where('clientId', '==', model.id)
    .where('model_type', '==', model.type || 'unknown')
    .get()
  let newest = null
  let newestDate = null
  snapshot.docs.forEach(doc => {
    const data = doc.data()
    if (!data.date_completed || data.simulationId) return
    const queuedAt = data.date_queued ? new Date(data.date_queued) : null
    if (!newest || (queuedAt && (!newestDate || queuedAt > newestDate))) {
      newest = data
      newestDate = queuedAt
    }
  })
  if (!newest) return undefined
  return newest.ngtorch_override || null
}

// The override the model itself was built with, for the two kinds of model ngtorch builds.
export async function buildOverride (model) {
  if (model.ekrm_id) {
    const doc = await firebase.db.collection('ekrm').doc(model.ekrm_id).get()
    return doc.exists ? doc.data().ngtorch_override || null : null
  }
  if (model.automatic_creation) {
    const snapshot = await firebase.db.collection('auto_model_requests').where('clientId', '==', model.id).limit(1).get()
    return snapshot.empty ? null : snapshot.docs[0].data().ngtorch_override || null
  }
  return null
}
